import { useMemo, useState } from 'react'
import { Section, TableScroller, useBearer, usePagedQuery } from './_admin-utils'
import { listTickets } from '@/lib/coreApi'

interface Ticket {
  id: string;
  status: string;
  priority: string;
  category: string;
  subject: string;
  created_by: string;
  created_at: string;
  assignee_id: string;
}

interface Shortcut {
  id: string;
  label: string;
  category?: string;
  createdSince?: string;
}

interface FetcherParams {
  page: number;
  pageSize: number;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'auto_resolved', label: 'Auto-resolved' },
  { value: 'closed', label: 'Closed' },
]

const STATUS_LABEL = {
  queued: 'Queued',
  in_progress: 'In progress',
  auto_resolved: 'Auto-resolved',
  closed: 'Closed',
}

const PRIORITY_LABEL = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
}

const SHORTCUTS = [
  { id: 'today', label: 'Today', createdSince: 'today' },
  { id: 'credits', label: 'Credits', category: 'credits' },
  { id: 'onboarding', label: 'Onboarding', category: 'onboarding' },
  { id: 'locker', label: 'Locker Room', category: 'locker_room' },
]

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

function startOfTodayIso() {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  return now.toISOString()
}

export default function AdminSupportDashboard() {
  const bearer = useBearer()
  const [status, setStatus] = useState('queued')
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [createdSince, setCreatedSince] = useState('')
  const [activeShortcut, setActiveShortcut] = useState('')

  const fetcher = useMemo(() => {
    return async ({ page, pageSize }: FetcherParams) => {
      if (!bearer) return { items: [], total: 0 }
      const result = await listTickets({
        page,
        page_size: pageSize,
        scope: 'admin',
        status: status && status !== 'all' ? status : undefined,
        q: search.trim() || undefined,
        category: category || undefined,
        created_since: createdSince || undefined,
      })
      return { items: result.data.items || [], total: result.data.total || 0 }
    }
  }, [bearer, status, search, category, createdSince])

  const { items, page, pageCount, loading, error, load } = usePagedQuery<Ticket>(fetcher, [fetcher], 30)

  const applyShortcut = (shortcut: Shortcut) => {
    if (activeShortcut === shortcut.id) {
      setActiveShortcut('')
      setCategory('')
      setCreatedSince('')
      return
    }
    setActiveShortcut(shortcut.id)
    setCategory(shortcut.category || '')
    if (shortcut.createdSince === 'today') {
      setCreatedSince(startOfTodayIso())
    } else if (typeof shortcut.createdSince === 'string') {
      setCreatedSince(shortcut.createdSince)
    } else {
      setCreatedSince('')
    }
  }

  return (
    <Section
      title="Support Dashboard"
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="rounded-full border border-white/70 bg-shell/40 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search subject"
            className="rounded-full border border-white/70 bg-shell/40 px-3 py-1 text-sm"
          />
          <button
            type="button"
            onClick={() => load(page)}
            className="rounded-full border border-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell"
          >
            Refresh
          </button>
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-cocoa/60">
        {SHORTCUTS.map((shortcut) => (
          <button
            key={shortcut.id}
            type="button"
            onClick={() => applyShortcut(shortcut)}
            className={`rounded-full border px-3 py-1 transition ${
              activeShortcut === shortcut.id
                ? 'border-cocoa bg-cocoa text-white'
                : 'border-white/70 text-cocoa hover:bg-shell'
            }`}
          >
            {shortcut.label}
          </button>
        ))}
        {activeShortcut ? (
          <button
            type="button"
            onClick={() => {
              setActiveShortcut('')
              setCategory('')
              setCreatedSince('')
            }}
            className="rounded-full border border-white/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-cocoa"
          >
            Clear
          </button>
        ) : null}
      </div>
      {loading ? (
        <p className="text-sm text-cocoa/70">Loading tickets…</p>
      ) : error ? (
        <p className="text-sm text-brick">{error}</p>
      ) : items.length ? (
        <TableScroller minWidth={960}>
          <table className="min-w-full divide-y divide-white/60 text-sm text-cocoa/80">
            <thead className="bg-shell/60 text-xs uppercase tracking-wide text-cocoa/60">
              <tr>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Priority</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Subject</th>
                <th className="px-3 py-2 text-left">Requester</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-left">Assignee</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/60">
              {items.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-shell/40">
                  <td className="px-3 py-2 font-semibold">{STATUS_LABEL[ticket.status as keyof typeof STATUS_LABEL] || ticket.status || '—'}</td>
                  <td className="px-3 py-2 capitalize">{PRIORITY_LABEL[ticket.priority as keyof typeof PRIORITY_LABEL] || ticket.priority || '—'}</td>
                  <td className="px-3 py-2 capitalize">{ticket.category || 'general'}</td>
                  <td className="px-3 py-2 font-medium text-cocoa">{ticket.subject || '—'}</td>
                  <td className="px-3 py-2 text-xs text-cocoa/70">{ticket.created_by || '—'}</td>
                  <td className="px-3 py-2 text-xs text-cocoa/70">{formatDate(ticket.created_at)}</td>
                  <td className="px-3 py-2 text-xs text-cocoa/70">{ticket.assignee_id || 'Unassigned'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      ) : (
        <p className="text-sm text-cocoa/70">No tickets found.</p>
      )}
      <div className="mt-4 flex items-center justify-between text-xs uppercase tracking-wide text-cocoa/60">
        <span>
          Showing page {page} of {pageCount}
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => load(Math.max(1, page - 1))}
            className="rounded-full border border-white/70 px-3 py-1 text-cocoa disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => load(Math.min(page + 1, pageCount))}
            className="rounded-full border border-white/70 px-3 py-1 text-cocoa disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </Section>
  )
}
